import { Spinner } from '@repo/ui/components/spinner';
import { cn } from '@repo/ui/lib/utils';

/**
 * Suspense fallback used by the root route.
 *
 * Renders a centered, gently-pulsing Scout logo over a pulsing progress
 * bar. The whole thing fades in to soften the route transition and
 * respects `prefers-reduced-motion` via Tailwind's `motion-safe` /
 * `motion-reduce` variants — no JS branching required.
 */
export function PageLoader({ className }: { className?: string }) {
	return (
		<div
			role='status'
			aria-live='polite'
			aria-label='Loading page'
			className={cn(
				'flex flex-1 flex-col items-center justify-center gap-6 px-6 py-16',
				'animate-in fade-in-0 duration-300 motion-reduce:animate-none',
				className,
			)}
		>
			<div className='motion-safe:animate-pulse'>
				<img
					src='/favicon.svg'
					alt=''
					aria-hidden='true'
					width={56}
					height={56}
					className='size-14 select-none drop-shadow-sm'
				/>
			</div>

			<div className='flex flex-col items-center gap-2'>
				<Spinner className='size-4 text-muted-foreground' />
				<span className='text-muted-foreground text-xs tracking-wide uppercase'>Loading</span>
			</div>

			{/* Indeterminate-looking bar. motion-safe pulses the foreground pill; with
			    reduced motion we still show a static half-filled bar as a stable cue. */}
			<div aria-hidden='true' className='bg-muted/40 relative h-1 w-48 overflow-hidden rounded-full'>
				<div className='bg-foreground/60 motion-safe:animate-pulse absolute inset-y-0 left-0 w-1/2 rounded-full motion-reduce:left-1/4 motion-reduce:w-1/2' />
			</div>
		</div>
	);
}
